import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { BreadCrumbService } from '@core/services/bread-crumb.service';
import { ButtonDirective } from '@shared/directives/button.directive';
import { TableRefreshComponent } from 'app/ui/table-refresh/table-refresh.component';
import { FiltersComponent } from '@shared/ui/filters/filters.component';
import { IFILTER } from 'app/app.constants';
import { TableNavigatorComponent } from '@shared/ui/table-navigator/table-navigator.component';
import {
  DropdownComponent,
  DropDownModel,
} from '@shared/ui/dropdown/dropdown.component';
import { dropdownMenuAnimation } from '@shared/animations/animations';
import { BehaviorSubject, Observable, Subscription, take } from 'rxjs';
import { TableSkeletonLoaderComponent } from '@shared/ui/table-skeleton-loader/table-skeleton-loader.component';
import { Store } from '@ngxs/store';
import { InputLabelComponent } from '@shared/ui/input-label/input-label.component';
import { TableSelectors } from 'app/auth/table-store/table.selectors';
import { TableActions } from 'app/auth/table-store/table.actions';
import { RouterOutlet } from '@angular/router';
import { Pagination } from '@core/models/pagination.model';
import { PermissionDirective } from '@shared/directives/permission.directive';
import {
  AdminNotification,
  NotificationStatus,
  PushType,
} from '@core/models/admin/account/admin-notification.model';
import { NotificationService } from '@core/services/notification.service';

const TABLE_KEY = 'notificationLogs';
const PAGE_SIZE = 10;

@Component({
  selector: 'app-notification-logs',
  standalone: true,
  imports: [
    CommonModule,
    ButtonDirective,
    TableRefreshComponent,
    FiltersComponent,
    TableNavigatorComponent,
    DropdownComponent,
    TableSkeletonLoaderComponent,
    InputLabelComponent,
    RouterOutlet,
    PermissionDirective,
  ],
  animations: [dropdownMenuAnimation],
  templateUrl: './notification-logs.html',
  styleUrls: ['./notification-logs.scss'],
})
export class NotificationsLogsComponent implements OnInit, OnDestroy {
  breadcrumbService = inject(BreadCrumbService);
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  private store = inject(Store);
  private notificationService = inject(NotificationService);

  isFilterActive = false;
  hadFilterBefore = false;

  allNotifications: AdminNotification[] = [];

  private notificationListSubject = new BehaviorSubject<{
    data: AdminNotification[];
    total_count: number;
  }>({ data: [], total_count: 0 });
  notificationList$ = this.notificationListSubject.asObservable();

  actionList: DropDownModel[] = [];
  isFetchingNotifications$!: Observable<boolean>;
  totalCount = 0;
  statsTotalCount = 0;
  statsSentCount = 0;
  statsFailedCount = 0;

  subs: Subscription[] = [];

  get totalDelivered(): number { return this.statsSentCount; }
  get totalFailed(): number { return this.statsFailedCount; }

  get deliveryRate(): string {
    if (this.statsTotalCount === 0) return '0.0%';
    return ((this.statsSentCount / this.statsTotalCount) * 100).toFixed(1) + '%';
  }

  get failureRate(): string {
    if (this.statsTotalCount === 0) return '0.0%';
    return ((this.statsFailedCount / this.statsTotalCount) * 100).toFixed(1) + '%';
  }

  // ─────────────────────────────────────────────────────────────────────────

  filterStructure: IFILTER[] = [
    {
      filterKey: 'status',
      label: 'Status',
      type: 'dropdown',
      dropdown: [
        { key: 'PENDING', value: 'Pending' },
        { key: 'SENT', value: 'Sent' },
        { key: 'SAVE_AS_DRAFT', value: 'Draft' },
        { key: 'FAILED', value: 'Failed' },
      ],
    },
    {
      filterKey: 'push_type',
      label: 'Push Type',
      type: 'dropdown',
      dropdown: [
        { key: 'RICH', value: 'Rich' },
        { key: 'INTERACTIVE', value: 'Interactive' },
        { key: 'SILENT', value: 'Silent' },
      ],
    },
    {
      filterKey: 'trigger_type',
      label: 'Trigger Type',
      type: 'dropdown',
      dropdown: [
        { key: 'MANUAL', value: 'Manual' },
        { key: 'FAILED_LOGIN', value: 'Failed Login' },
        { key: 'ORDER_FAILED', value: 'Order Failed' },
      ],
    },
    {
      filterKey: 'audience',
      label: 'Audience',
      type: 'dropdown',
      dropdown: [
        { key: 'ALL_USER', value: 'All Users' },
        { key: 'ACTIVE_USER', value: 'Active Users' },
        { key: 'INACTIVE_USER', value: 'Inactive Users' },
        { key: 'INCOMPLETE_PROFILE_USER', value: 'Incomplete Profile' },
        { key: 'NEW_USER', value: 'New Users' },
        { key: 'SPECIFIC_LOCATION_USER', value: 'Specific Location' },
        { key: 'SPECIFIC_USERS', value: 'Specific Users' },
      ],
    },
    {
      filterKey: 'start_date',
      label: 'From Date',
      type: 'date',
    },
    {
      filterKey: 'end_date',
      label: 'To Date',
      type: 'date',
    },
  ];

  constructor() {
    this.isFetchingNotifications$ = this.store.select(
      TableSelectors.getTableLoading(TABLE_KEY)
    );
  }

  ngOnInit(): void {
    this.breadcrumbService.setPageBreadCrumb(this.activatedRoute);
    this.subscribeToTableData();
    this.notificationFiltersEventListener();
    this.loadList();
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  // Mirror the NGXS table state into the { data, total_count } subject.
  private subscribeToTableData(): void {
    const dataSub = this.store
      .select(TableSelectors.getTableData(TABLE_KEY))
      .subscribe((rows: AdminNotification[]) => {
        this.allNotifications = rows || [];
        this.statsSentCount = this.allNotifications.reduce((s, n) => s + (n.success_count ?? 0), 0);
        this.statsFailedCount = this.allNotifications.reduce((s, n) => s + (n.failed_count ?? 0), 0);
        this.notificationListSubject.next({
          data: this.allNotifications,
          total_count: this.totalCount,
        });
      });

    const countSub = this.store
      .select(TableSelectors.getTableTotalCount(TABLE_KEY))
      .subscribe((count) => {
        this.totalCount = count || 0;
        this.statsTotalCount = this.totalCount;
        this.notificationListSubject.next({
          data: this.allNotifications,
          total_count: this.totalCount,
        });
      });

    this.subs.push(dataSub, countSub);
  }

  // Initial load — only fetch if the table is empty (cache-friendly).
  loadList(page = 1): void {
    const sub = this.store
      .select(TableSelectors.getTableData(TABLE_KEY))
      .pipe(take(1))
      .subscribe((rows: AdminNotification[]) => {
        const hasData = Array.isArray(rows) && rows.length > 0;
        if (!hasData) {
          this.store.dispatch(
            new TableActions.LoadTableData({
              tableKey: TABLE_KEY,
              params: new Pagination({
                skip: (page - 1) * PAGE_SIZE,
                size: PAGE_SIZE,
              }),
            })
          );
        }
      });
    this.subs.push(sub);
  }

  // Translate URL query params into the endpoint's filter query string.
  notificationFiltersEventListener(): void {
    const sub = this.activatedRoute.queryParams.subscribe((params) => {
      let query = '';

      if (params['status']) {
        query += `status=${params['status']}&`;
      }
      if (params['push_type']) {
        query += `push_type=${params['push_type']}&`;
      }
      if (params['trigger_type']) {
        query += `trigger_type=${params['trigger_type']}&`;
      }
      if (params['audience']) {
        query += `audience=${params['audience']}&`;
      }
      if (params['start_date']) {
        query += `start_date=${params['start_date']}&`;
      }
      if (params['end_date']) {
        query += `end_date=${params['end_date']}&`;
      }
      if (params['search']) {
        query += `search_text=${encodeURIComponent(params['search'])}&`;
      }

      const rawPage = params['page'] ? parseInt(params['page']) : 1;
      const page = rawPage > 1 ? rawPage - 1 : 0;
      const skip = page * PAGE_SIZE;

      if (query) {
        query += `skip=${skip}&take=${PAGE_SIZE}`;
        this.isFilterActive = true;
        this.hadFilterBefore = true;
        this.store.dispatch(
          new TableActions.FilterNotificationLog({ filter: query })
        );
      } else {
        this.isFilterActive = false;
        if (this.hadFilterBefore || params['page']) {
          this.hadFilterBefore = false;
          this.store.dispatch(
            new TableActions.LoadTableData({
              tableKey: TABLE_KEY,
              params: new Pagination({ skip, size: PAGE_SIZE }),
            })
          );
        }
      }
    });
    this.subs.push(sub);
  }

  navigate(currentPage: number): void {
    this.router.navigate([], {
      queryParams: { page: currentPage },
      queryParamsHandling: 'merge',
    });
  }

  // ─── Display helpers ──────────────────────────────────────────────────────

  // Friendly label for the Type column (drives its colour styling too).
  pushTypeLabel(type: PushType | null): string {
    switch (type) {
      case 'RICH':
        return 'Rich';
      case 'INTERACTIVE':
        return 'Interactive';
      case 'SILENT':
        return 'Silent';
      default:
        return '-';
    }
  }

  statusBg(status: NotificationStatus): string {
    switch (status) {
      case 'SENT':
        return 'bg-mango-paleGreen';
      case 'FAILED':
        return 'bg-mango-paleRed';
      case 'PENDING':
        return 'bg-mango-papayaWhip';
      default:
        return 'bg-mango-grayF7';
    }
  }

  statusText(status: NotificationStatus): string {
    switch (status) {
      case 'SENT':
        return 'text-mango-pineGreen';
      case 'FAILED':
        return 'text-mango-red707';
      case 'PENDING':
        return 'text-mango';
      default:
        return 'text-mango-gray500';
    }
  }

  statusLabel(status: NotificationStatus): string {
    return status === 'SAVE_AS_DRAFT' ? 'DRAFT' : status;
  }

  // ─── Row actions ──────────────────────────────────────────────────────────

  initDropdownList(notification: AdminNotification) {
    return (this.actionList = [
      {
        title: 'View Details',
        action: () => this.viewDetails(notification),
        show: true,
      },
      {
        title: 'Resend',
        action: () => this.resendNotification(notification),
        show: notification.status === 'FAILED',
      },
    ]);
  }

  viewDetails(notification: AdminNotification) {
    this.router.navigate([notification.id], {
      relativeTo: this.activatedRoute,
      state: { notification },
    });
  }

  resendNotification(notification: AdminNotification) {
    console.log('Resending notification:', notification);
  }
}
